import React, { Component, Fragment } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '../../../component/form/Grid';
import withStyles from '@mui/styles/withStyles';
import PrimaryButton, { AltButton, OutlineButton } from '../../../component/form/PrimaryButton';
import { Query } from '../../../reactApolloCompat';
import gql from 'graphql-tag';
import { getMyClient } from '../../../apollo';
import { joinDefined, prettyJSON } from '../../../util/strings';
import { niceDateTimeFromString } from '../../../util/date';
import CloseIcon from '../../../component/icon/CloseIcon';
import Spinner from '../../../component/Spinner';
import LinearProgress from '@mui/material/LinearProgress';
import Hidden from '../../../component/Hidden';

class ChangelogModal extends Component {
    state = {
        open: false
    };

    onCancel = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        this.setState({ open: false });
    };

    handleScroll = (e, onLoadMore) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom) {
            // Your logic for when the user reaches the bottom goes here
            onLoadMore();
        }
    };

    render() {
        const { open } = this.state;
        const { legacyKey, funeralID, classes } = this.props;
        if (!(funeralID > 0)) return null;
        return (
            <Fragment>
                <OutlineButton text="View Audit Log..." onClick={() => this.setState({ open: true })} />
                {!!open && (
                    <Dialog open={!!open} className={classes.root} onClose={this.onCancel}>
                        <DialogTitle className={classes.modalTitle}>Audit Log: Funeral {legacyKey}</DialogTitle>
                        <Query
                            client={getMyClient()}
                            query={queryChangeLog}
                            variables={{ funeralID: Number(funeralID), limit: 25, offset: 0 }}
                            fetchPolicy="network-only"
                            notifyOnNetworkStatusChange
                        >
                            {results => this.renderForm(results)}
                        </Query>
                    </Dialog>
                )}
            </Fragment>
        );
    }

    renderForm = ({ error, loading, data, fetchMore }) => {
        const { classes } = this.props;
        const { readDataChangeRecords } = data || {};

        const onLoadMore = async () => {
            if (loading || !(readDataChangeRecords && readDataChangeRecords.pageInfo.hasNextPage)) {
                return null;
            }
            try {
                await fetchMore({
                    variables: { offset: readDataChangeRecords.edges.length },
                    updateQuery: (prev, { fetchMoreResult }) => {
                        if (!fetchMoreResult) return prev;
                        return Object.assign(
                            {},
                            {
                                readDataChangeRecords: {
                                    edges: [
                                        ...prev.readDataChangeRecords.edges,
                                        ...fetchMoreResult.readDataChangeRecords.edges
                                    ],
                                    __typename: 'readDataChangeRecordsConnection',
                                    pageInfo: fetchMoreResult.readDataChangeRecords.pageInfo
                                }
                            }
                        );
                    }
                });
            } catch (e) {
                // when someone changes the search before the result comes back, it throws error.
            }
        };

        return (
            <Fragment>
                <DialogContent className={classes.modalContent} onScroll={e => this.handleScroll(e, onLoadMore)}>
                    <Grid container spacing={2}>
                        <div className={classes.loading}>
                            {(!!loading && (
                                <>
                                    <LinearProgress /> Loading...
                                </>
                            )) ||
                                (!!error && `Error! ${error}`)}
                        </div>
                        {!!readDataChangeRecords &&
                            readDataChangeRecords.edges &&
                            readDataChangeRecords.edges.map(e => this.renderEntry(e.node))}
                    </Grid>
                    {!!readDataChangeRecords && (
                        <div className={classes.loadMore}>
                            {readDataChangeRecords.pageInfo.hasNextPage ? (
                                <PrimaryButton onClick={onLoadMore} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Spinner /> <span>Loading more...</span>
                                        </>
                                    ) : (
                                        'Load more changelog'
                                    )}
                                </PrimaryButton>
                            ) : (
                                'End of changelog.'
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions className={classes.modalFooter}>
                    <div style={{ flexGrow: 2 }}>
                        <AltButton
                            onClick={() =>
                                document
                                    .querySelectorAll('.prettyJSON-label')
                                    .forEach(e => e.classList.add('prettyJSON-open'))
                            }
                        >
                            + <Hidden smDown> Expand All</Hidden>
                        </AltButton>
                        &nbsp;
                        <AltButton
                            onClick={() =>
                                document
                                    .querySelectorAll('.prettyJSON-label')
                                    .forEach(e => e.classList.remove('prettyJSON-open'))
                            }
                        >
                            - <Hidden smDown> Collapse All</Hidden>
                        </AltButton>
                    </div>
                    <OutlineButton onClick={() => this.onCancel()} color="primary">
                        <CloseIcon />
                        <Hidden mdDown>Close</Hidden>
                    </OutlineButton>
                </DialogActions>
            </Fragment>
        );
    };

    renderEntry = entry => {
        const { classes } = this.props;
        const { input } = entry.Variables ? JSON.parse(entry.Variables || '') : {};
        const hasInput = input && !!Object.keys(input).length;
        return (
            <Grid item xs={12} key={entry.ID}>
                <p>
                    On {niceDateTimeFromString(entry.Created, true)},{' '}
                    {joinDefined([entry.ChangedBy.FirstName, entry.ChangedBy.Surname], ' ') || entry.ChangedBy.Email}
                    {' wrote:'}
                </p>
                <div className={classes.prettyChanged}>
                    <div className={classes.prettyData}>{prettyJSON(hasInput ? entry.Variables : entry.Before)}</div>
                </div>
            </Grid>
        );
    };
}

const queryChangeLog = gql`
    query ReadChangelog($funeralID: Int!, $limit: Int, $offset: Int) {
        readDataChangeRecords(
            filter: { ChangeRecordID: { eq: $funeralID }, ChangeRecordClass: { eq: _FuneralManagerFuneral } }
            limit: $limit
            offset: $offset
        ) {
            edges {
                node {
                    ID
                    Created
                    ChangeType
                    Before
                    After
                    Variables
                    ChangedBy {
                        ID
                        Surname
                        FirstName
                        Email
                    }
                }
            }
            pageInfo {
                hasNextPage
            }
        }
    }
`;

const styles = theme => ({
    root: {
        '& > div:nth-child(2) > div': {
            maxWidth: 660,
            width: '98vw',
            height: 800,
            maxHeight: '94vh',
            [theme.breakpoints.down('lg')]: {
                margin: 16
            }
        }
    },
    modalTitle: {
        padding: 24,
        margin: 0,
        background: theme.palette.contentForeground[theme.funeralHome],
        color: '#FFFFFF',
        fontWeight: 'lighter',
        [theme.breakpoints.up('md')]: {
            fontSize: 26
        }
    },
    modalFooter: {
        padding: '10px 20px 20px',
        margin: 0
    },
    modalContent: {
        position: 'relative',
        paddingTop: `${theme.spacing(2)}!important`,
        height: 'auto',
        overflowY: 'auto',
        overflowX: 'auto',
        '& p': {
            color: theme.palette.contentForeground[theme.funeralHome]
        },
        [theme.breakpoints.up('md')]: {
            padding: '16px 48px 24px 48px'
        }
    },
    prettyChanged: {
        borderBottom: '2px solid #00000033',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap'
    },
    prettyData: { minWidth: 240, width: '50%', padding: 16, paddingTop: 0 },
    loading: { width: '100%', position: 'sticky', top: 0 },
    loadMore: { textAlign: 'center', margin: '12px auto 6px' }
});

export default withStyles(styles)(ChangelogModal);
